import {
  Badge,
  Button,
  Box,
  Flex,
  FlexProps,
  Heading,
  List,
  ListItem,
} from '@chakra-ui/react';
import { Icons } from '@texas/components/shared/Icons';
import { memo, RefObject, useEffect, useState } from 'react';

export interface WarningItem {
  id?: string;
  targetId?: string;
  groupId?: string;
  message: string;
  mandatory: boolean;
}

interface Props extends FlexProps {
  containerRef?: RefObject<HTMLElement>;
  warnings?: readonly WarningItem[];
  title: string;
  onNavigate?: (warning: WarningItem) => void;
}

export const WarningSummary = memo(function WarningSummary({
  containerRef,
  warnings: providedWarnings,
  title,
  onNavigate,
  ...props
}: Props) {
  const [observedWarnings, setWarnings] = useState<WarningItem[]>([]);

  useEffect(() => {
    if (providedWarnings !== undefined) return;
    const container = containerRef?.current;
    if (!container) return;

    const updateWarnings = () => {
      const nextWarnings = Array.from(
        container.querySelectorAll<HTMLElement>(
          '[data-warning][data-warning-message]',
        ),
      )
        .map((element) => ({
          message: element.dataset.warningMessage?.trim() ?? '',
          mandatory: element.dataset.warning === '2',
        }))
        .filter((warning) => warning.message.length > 0);

      setWarnings((current) =>
        current.length === nextWarnings.length &&
        current.every(
          (warning, index) =>
            warning.message === nextWarnings[index].message &&
            warning.mandatory === nextWarnings[index].mandatory,
        )
          ? current
          : nextWarnings,
      );
    };

    updateWarnings();
    const observer = new MutationObserver(updateWarnings);
    observer.observe(container, {
      attributes: true,
      attributeFilter: ['data-warning', 'data-warning-message'],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [containerRef, providedWarnings]);

  const warnings = providedWarnings ?? observedWarnings;

  if (warnings.length === 0) return null;

  return (
    <Flex
      align="flex-start"
      gap={3}
      bg="orange.50"
      color="gray.800"
      _dark={{ bg: 'orange.900', color: 'gray.50' }}
      borderRadius="md"
      px={5}
      py={4}
      mb={5}
      aria-live="polite"
      {...props}
    >
      <Icons.AlertCircle boxSize={5} color="orange.500" flexShrink={0} />
      <Box>
        <Flex align="center" gap={2} mb={2}>
          <Heading size="sm">{title}</Heading>
          <Badge colorScheme="orange">{warnings.length}</Badge>
        </Flex>
        <List spacing={1} fontSize="sm">
          {warnings.map((warning, index) => (
            <ListItem key={warning.id ?? `${warning.message}-${index}`}>
              {onNavigate && warning.targetId ? (
                <Button
                  type="button"
                  variant="link"
                  fontSize="inherit"
                  fontWeight="normal"
                  color="inherit"
                  height="auto"
                  whiteSpace="normal"
                  textAlign="left"
                  textDecoration="underline"
                  justifyContent="flex-start"
                  aria-controls={warning.targetId}
                  onClick={() => onNavigate(warning)}
                >
                  {warning.message}
                </Button>
              ) : (
                warning.message
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Flex>
  );
},
haveSameProps);

function haveSameProps(previous: Props, next: Props) {
  const { warnings: previousWarnings, ...previousProps } = previous;
  const { warnings: nextWarnings, ...nextProps } = next;
  const keys = Object.keys(previousProps) as (keyof typeof previousProps)[];
  const sameProps =
    keys.length === Object.keys(nextProps).length &&
    keys.every((key) => previousProps[key] === nextProps[key]);
  if (!sameProps) return false;
  if (previousWarnings === nextWarnings) return true;
  if (!previousWarnings || !nextWarnings) return false;
  return (
    previousWarnings.length === nextWarnings.length &&
    previousWarnings.every((warning, index) => {
      const nextWarning = nextWarnings[index];
      return (
        warning.id === nextWarning.id &&
        warning.targetId === nextWarning.targetId &&
        warning.groupId === nextWarning.groupId &&
        warning.message === nextWarning.message &&
        warning.mandatory === nextWarning.mandatory
      );
    })
  );
}
